import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

import { GeoDataService } from '@core/data/api/geo/geo-data.service';
import { WindowRef } from '@vendor/window/window.component';
import { isNullOrUndefined } from '@core/utils/object';
import { $log } from '@core/utils/logger';
import { Country } from '@core/models/common/country';

@Injectable({ providedIn: 'root' })
export class PhoneUtilService {
    public readonly NATIONAL: number = 2;
    public readonly INTERNATIONAL: number = 1;
    public readonly DEFAULT_COUNTRY: string = 'us';

    public static COUNTRY_CODE_US = 'us';

    private countries: any[];
    private countryCodes: any;
    private allCountries: any[] = [];
    private currentFormat: string;
    private window: any;

    constructor(
        private windowRef: WindowRef,
        private geoDataService: GeoDataService
    ) {
        this.window = this.windowRef.nativeWindow;
        const intlTelInputGlobals = this.window.intlTelInputGlobals;
        // const publicPath = this.window.PUBLIC_PATH === '/' ? '' : this.window.PUBLIC_PATH;

        this.allCountries = intlTelInputGlobals.getCountryData();
        this.processCountryCodes();
    }

    getCountries() {
        return this.countries;
    }

    /**
     * Returns country data based on country code.
     * @param countryCode
     * @param ignoreOnlyCountriesOption
     * @param allowFail
     * @returns {*}
     */
    getCountryData(countryCode: string, ignoreOnlyCountriesOption, allowFail) {
        let countryList = ignoreOnlyCountriesOption
            ? this.allCountries
            : this.countries;
        if (isNullOrUndefined(countryCode) || countryCode.length <= 0) {
            countryCode = this.DEFAULT_COUNTRY;
        }

        const country: any = countryList.find(
            (c) => c.iso2 === countryCode.toLowerCase()
        );

        if (country) {
            return country;
        } else if (allowFail) {
            /* eslint-disable */
            throw new Error(
                "No country data for '" + countryCode.toLowerCase() + "'"
            );
            /* eslint-enable */
        } else {
            // No matching country data found.
            $log.error('No country data found for: ', countryCode);
            return null;
        }
    }

    /**
     * Returns a placeholder number, applying custom mask.
     * @param countryCode
     * @returns {string}
     */
    getPlaceholderNumber(countryCode = '', customAUPlaceholder = '') {
        countryCode = countryCode || '';
        let examplePhoneNumber = this.getExampleNumber(
            countryCode.toLowerCase()
        );
        return this.processTelephoneMasks(
            countryCode.toLowerCase(),
            examplePhoneNumber,
            customAUPlaceholder
        );
    }

    /**
     * Returns the default country code attempts to resolve via IP on startup.
     */
    getDefaultCountry(): Observable<Country> {
        return this.geoDataService.getCountry().pipe(
            map((country) => {
                if (
                    !country ||
                    isNullOrUndefined(country.country_code) ||
                    country.country_code.length <= 0
                ) {
                    country = {
                        country_code: this.DEFAULT_COUNTRY,
                    };
                }

                return Object.assign(new Country(), country);
            })
        );
    }

    /**
     * Formats phone number.
     * @param value
     * @param countryCode
     * @param phoneFormat
     * @returns {*}
     */
    formatNumber(value, countryCode, phoneFormat) {
        let currentFormat = phoneFormat;
        let res = value;
        if (this.window.intlTelInputUtils) {
            if (this.currentFormat) {
                this.currentFormat =
                    value[0] === '+'
                        ? this.window.intlTelInputUtils.numberFormat.INTERNATIONAL
                        : this.window.intlTelInputUtils.numberFormat.NATIONAL;
            }

            return this.window.intlTelInputUtils.formatNumber(
                value,
                countryCode,
                currentFormat
            );
        } else {
            return res;
        }
    }

    /**
     * Validates phone number.
     * @param value
     * @param countryCode
     * @returns {*}
     */
    isValidNumber(value, countryCode?) {
        return (
            this.window.intlTelInputUtils &&
            this.window.intlTelInputUtils.isValidNumber(value, countryCode)
        );
    }

    /**
     * Attempt to match the country code from the phone number.
     * @param phone
     * @returns {*}
     */
    matchCountryData(phone: string, defaultCountry?: Country): any {
        let matchedCountryData;
        if (phone && phone.length > 0) {
            let rawValue = phone.replace(/[^0-9]/g, '');
            let rawCountryMatch;
            let processedPhone = '';
            let matchedCountries = [];
            let matchingCountryOnly = [];
            let matchingCountryByAreaCode = [];
            for (let i = 0, m = rawValue.length; i < m; i++) {
                processedPhone = rawValue.substr(0, i + 1);

                // Search for matching dial code
                rawCountryMatch = false;
                for (let c = 0, n = this.allCountries.length; c < n; c++) {
                    if (this.allCountries[c].dialCode === processedPhone) {
                        rawCountryMatch = this.allCountries[c];
                        matchedCountries.push(rawCountryMatch);

                        // Attempt to match area code if required
                        if (
                            rawCountryMatch.areaCodes &&
                            rawCountryMatch.areaCodes.length > 0
                        ) {
                            let rawValueNoDialCode = rawValue.substr(
                                rawCountryMatch.dialCode.length,
                                rawValue.length
                            );
                            let countryAreaCode;
                            let isAreaCodeMatched = false;

                            for (
                                let d = 0,
                                    mt = rawCountryMatch.areaCodes.length;
                                d < mt;
                                d++
                            ) {
                                countryAreaCode = rawCountryMatch.areaCodes[d];
                                if (
                                    rawValueNoDialCode.indexOf(
                                        countryAreaCode
                                    ) === 0
                                ) {
                                    isAreaCodeMatched = true;
                                    break;
                                }
                            }

                            // Matching country by area code
                            if (isAreaCodeMatched) {
                                matchingCountryByAreaCode.push(rawCountryMatch);
                            }
                        } else {
                            matchingCountryOnly.push(rawCountryMatch);
                        }
                    }
                }
            }

            // Return matching country with area code first
            if (matchingCountryByAreaCode.length > 0) {
                matchedCountryData = matchingCountryByAreaCode[0];
            } else if (matchingCountryOnly.length > 0) {
                // Attempt to match by geo detected country
                if (defaultCountry) {
                    const defaultCountryCode: string =
                        defaultCountry &&
                        defaultCountry.country_code &&
                        defaultCountry.country_code.toLowerCase();
                    const filteredMatches: any[] = matchingCountryOnly.filter(
                        (matchedCountry) =>
                            matchedCountry.iso2 === defaultCountryCode
                    );
                    if (filteredMatches.length > 0) {
                        matchedCountryData = filteredMatches[0];
                    }
                }

                if (!matchedCountryData) {
                    // Drop back to matching country only
                    matchedCountryData = matchingCountryOnly[0];
                }
            }
        }

        return matchedCountryData;
    }

    /**
     * Returns a valid example phone number by country code.
     * @param countryCode
     * @returns {*}
     */
    getExampleNumber(countryCode = '') {
        countryCode = countryCode || '';
        return this.window.intlTelInputUtils?.getExampleNumber(
            countryCode.toLowerCase(),
            true,
            this.window.intlTelInputUtils.numberType.MOBILE
        ) || '';
    }

    loadUpUtilities() {
        const intlTelInputGlobals = this.window.intlTelInputGlobals;
        intlTelInputGlobals.loadUtils('assets/js/intlTelInputUtils.js');
    }

    /**
     * Replaces the '123456789' part on telphone input masks with 'XXX' maintaining whitespaces
     * @param {any} countryCodeIso2
     * @param {any} countryPlaceHolder
     */
    private processTelephoneMasks(
        countryCodeIso2,
        countryPlaceHolder,
        customAUPlaceholder = ''
    ) {
        if (countryCodeIso2 && countryCodeIso2.toLowerCase() === 'au') {
            if (customAUPlaceholder && customAUPlaceholder.length > 0) {
                return customAUPlaceholder;
            } else {
                return '04XX XXX XXX';
            }
        } else {
            let result = countryPlaceHolder.replace(/[0-9]/g, 'X');
            return result;
        }
    }

    private addCountryCode(iso2, dialCode, priority) {
        if (!(dialCode in this.countryCodes)) {
            this.countryCodes[dialCode] = [];
        }
        let index = priority || 0;
        this.countryCodes[dialCode][index] = iso2;
    }

    private processCountryCodes() {
        this.countryCodes = {};
        this.allCountries.forEach((c) => {
            this.addCountryCode(c.iso2, c.dialCode, c.priority);

            // area codes
            if (c.areaCodes) {
                c.areaCodes.forEach((ac, i) => {
                    this.addCountryCode(
                        c.iso2,
                        c.dialCode + c.areaCodes[i],
                        null
                    );
                });
            }
        });

        this.countries = this.allCountries;
    }
}
